// Notice sidebar — 1차 메뉴(카테고리)만 표시. 2차(세부탭)는 본문 상단 탭.
// 드라이브처럼 구분선(섹션) + 빈칸 우클릭 컨텍스트 메뉴 지원. 폴더트리 형태.
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../shared/api/client";
import { I } from "../shared/Icon";
import { toast } from "../shared/stores/toasts";
import { useAuth } from "../shared/stores/auth";
import { ContextMenu, type CtxItem } from "./SubNav";

export type NoticeCategory = {
  id: number; code: string; label: string; color: string;
  sort_order: number; is_active: boolean;
  parent_id: number | null; min_rank: number;
};

// Rank options mirror backend app/core/roles.py ROLE_RANK thresholds.
const RANK_OPTIONS = [
  { v: 0, label: "전체 공개" },
  { v: 20, label: "사원 이상" },
  { v: 30, label: "팀장 이상" },
  { v: 60, label: "실장 이상" },
  { v: 80, label: "임원 이상" },
];
const rankLabel = (v: number) => RANK_OPTIONS.find(o => o.v === v)?.label ?? `직급 ${v}+`;
const rankShort = (v: number) => (v <= 0 ? "" : rankLabel(v).replace(" 이상", "+").replace(" 공개", ""));

const HEX: Record<string, string> = {
  info: "#3b82f6", danger: "#ef4444", warning: "#f59e0b", success: "#22c55e", default: "#94a3b8",
};
const dotColor = (c: string) => HEX[c] ?? HEX.info;

// ── 구분선(섹션) — 드라이브와 동일한 ❖ 센티넬 방식 ───────────────────────────────
const SECTION_MARK = "❖";
const isDivider = (c: NoticeCategory) => c.label.startsWith(SECTION_MARK);
function parseSection(label: string): { text: string; uid: string | null } {
  const m = label.match(/^❖([\s\S]*)❖(\d+)$/);
  if (m) return { text: m[1], uid: m[2] };
  if (label.startsWith(SECTION_MARK)) return { text: label.slice(SECTION_MARK.length), uid: null };
  return { text: "", uid: null };
}
const buildSectionName = (text: string, uid?: string | null) => `${SECTION_MARK}${text}${SECTION_MARK}${uid ?? Date.now()}`;

function genCode(label: string, existing: Set<string>, prefix = "") {
  let base = (prefix || label).toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  if (!base) base = "CAT" + Date.now().toString(36).toUpperCase();
  base = base.slice(0, 24);
  let code = base, i = 1;
  while (existing.has(code)) code = `${base}_${i++}`;
  return code.slice(0, 30);
}

// ── Inline input (add / rename) ─────────────────────────────────────────────────
function InlineInput({ initial, placeholder, onCommit, onCancel }: {
  initial?: string; placeholder?: string; onCommit: (v: string) => void; onCancel: () => void;
}) {
  const [v, setV] = useState(initial ?? "");
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  return (
    <input
      ref={ref} value={v} placeholder={placeholder}
      onChange={e => setV(e.target.value)}
      onKeyDown={e => {
        if (e.key === "Enter") { e.stopPropagation(); const t = v.trim(); t ? onCommit(t) : onCancel(); }
        if (e.key === "Escape") { e.stopPropagation(); onCancel(); }
      }}
      onBlur={() => { const t = v.trim(); t ? onCommit(t) : onCancel(); }}
      onClick={e => e.stopPropagation()}
      style={{ flex: 1, fontSize: 12, padding: "2px 6px", border: "1px solid var(--p-400,#818cf8)", borderRadius: 4, outline: "none", background: "var(--bg-0)", color: "var(--text)", fontFamily: "inherit", minWidth: 0 }}
    />
  );
}

const iconBtn: React.CSSProperties = {
  background: "none", border: "none", cursor: "pointer", width: 20, height: 20,
  borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center",
  fontSize: 11, color: "var(--text-3)", padding: 0, flexShrink: 0,
};

export function NoticeSidebar({ onCollapse }: { onCollapse: () => void }) {
  const nav = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const isAdmin = useAuth(s => s.canManageMenu("notice") || s.hasRole("SUPER_ADMIN"));

  const [cats, setCats] = useState<NoticeCategory[]>([]);
  const [addingTop, setAddingTop] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [ctx, setCtx] = useState<{ x: number; y: number; cat: NoticeCategory } | null>(null);
  const [bgCtx, setBgCtx] = useState<{ x: number; y: number } | null>(null);
  const dragId = useRef<number | null>(null);
  const [dropId, setDropId] = useState<number | null>(null);

  const activeFilter = params.get("filter") || "";
  const onNoticeRoot = location.pathname === "/notice" && !activeFilter;

  const reload = () =>
    api.get<NoticeCategory[]>("/notice/categories").then(r => setCats(r.data)).catch(() => {});

  useEffect(() => {
    reload();
    const h = () => reload();
    window.addEventListener("notice-categories-updated", h);
    return () => window.removeEventListener("notice-categories-updated", h);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 1차 = 최상위 항목(카테고리 + 구분선). 세부탭(parent 있음)은 사이드바에 표시하지 않음.
  const topItems = cats.filter(c => !c.parent_id);
  const allCodes = new Set(cats.map(c => c.code));

  const goFilter = (code: string | null) => nav(code ? `/notice?filter=${code}` : "/notice");
  const fire = () => window.dispatchEvent(new Event("notice-categories-updated"));

  // ── Admin mutations ──────────────────────────────────────────────────────────
  const addCategory = async (label: string) => {
    try {
      await api.post("/notice/categories", { code: genCode(label, allCodes), label, color: "info", parent_id: null, min_rank: 0 });
      toast.success("카테고리 추가됨"); setAddingTop(false); reload(); fire();
    } catch (e: any) { toast.error("추가 실패", e?.response?.data?.error?.message ?? ""); }
  };
  const addSubTab = async (parentId: number) => {
    const label = prompt("세부탭 이름")?.trim();
    if (!label) return;
    try {
      await api.post("/notice/categories", { code: genCode(label, allCodes), label, color: "info", parent_id: parentId, min_rank: 0 });
      toast.success("세부탭 추가됨"); reload(); fire();
    } catch (e: any) { toast.error("추가 실패", e?.response?.data?.error?.message ?? ""); }
  };
  const addDivider = async () => {
    const text = prompt("구분선 이름 (비워두면 선만 표시)");
    if (text === null) return;
    try {
      await api.post("/notice/categories", { code: genCode("", allCodes, "DIV"), label: buildSectionName(text.trim()), color: "default", parent_id: null, min_rank: 0 });
      reload(); fire();
    } catch (e: any) { toast.error("구분선 추가 실패", e?.response?.data?.error?.message ?? ""); }
  };
  const renameCat = async (cat: NoticeCategory, text: string) => {
    setRenaming(null);
    const label = isDivider(cat) ? buildSectionName(text, parseSection(cat.label).uid) : text;
    try { await api.patch(`/notice/categories/${cat.code}`, { label }); reload(); fire(); }
    catch (e: any) { toast.error("이름 변경 실패", e?.response?.data?.error?.message ?? ""); }
  };
  const setRank = async (code: string, min_rank: number) => {
    try { await api.patch(`/notice/categories/${code}`, { min_rank }); toast.success(`권한: ${rankLabel(min_rank)}`); reload(); fire(); }
    catch (e: any) { toast.error("권한 변경 실패", e?.response?.data?.error?.message ?? ""); }
  };
  const removeCat = async (cat: NoticeCategory) => {
    const kids = cats.filter(c => c.parent_id === cat.id);
    if (!isDivider(cat)) {
      const msg = `"${cat.label}" 카테고리를 삭제하시겠습니까?${kids.length ? `\n하위 세부탭 ${kids.length}개도 함께 삭제됩니다.` : ""}`;
      if (!window.confirm(msg)) return;
    }
    try { await api.delete(`/notice/categories/${cat.code}`); toast.success("삭제됨"); reload(); fire(); }
    catch (e: any) { toast.error("삭제 실패", e?.response?.data?.error?.message ?? ""); }
  };

  // 최상위 항목 순서 변경 (카테고리 + 구분선 모두 포함)
  const reorder = async (codes: string[]) => {
    try { await api.put("/notice/categories/reorder", { codes }); reload(); fire(); }
    catch { toast.error("순서 변경 실패"); }
  };
  const moveByOne = (cat: NoticeCategory, dir: -1 | 1) => {
    const codes = topItems.map(c => c.code);
    const i = codes.indexOf(cat.code), j = i + dir;
    if (j < 0 || j >= codes.length) return;
    [codes[i], codes[j]] = [codes[j], codes[i]];
    reorder(codes);
  };
  const handleDrop = (target: NoticeCategory) => {
    const from = dragId.current; dragId.current = null; setDropId(null);
    if (from == null || from === target.id) return;
    const codes = topItems.map(c => c.code);
    const fromCode = topItems.find(c => c.id === from)?.code;
    if (!fromCode) return;
    const fi = codes.indexOf(fromCode), ti = codes.indexOf(target.code);
    codes.splice(ti, 0, codes.splice(fi, 1)[0]);
    reorder(codes);
  };

  const ctxItems = (cat: NoticeCategory): CtxItem[] => isDivider(cat) ? [
    { label: "이름 변경", icon: "✏️", onClick: () => setRenaming(cat.code) },
    { label: "위로 이동", icon: "↑", onClick: () => moveByOne(cat, -1) },
    { label: "아래로 이동", icon: "↓", onClick: () => moveByOne(cat, 1) },
    { sep: true },
    { label: "삭제", icon: "🗑️", onClick: () => removeCat(cat), danger: true },
  ] : [
    { label: "세부탭 추가", icon: "＃", onClick: () => addSubTab(cat.id) },
    { label: "이름 변경", icon: "✏️", onClick: () => setRenaming(cat.code) },
    { sep: true },
    { label: "위로 이동", icon: "↑", onClick: () => moveByOne(cat, -1) },
    { label: "아래로 이동", icon: "↓", onClick: () => moveByOne(cat, 1) },
    { label: "구분선 추가", icon: "―", onClick: addDivider },
    { sep: true },
    ...RANK_OPTIONS.map(o => ({ label: `${cat.min_rank === o.v ? "● " : "○ "}권한: ${o.label}`, onClick: () => setRank(cat.code, o.v) } as CtxItem)),
    { sep: true },
    { label: "삭제", icon: "🗑️", onClick: () => removeCat(cat), danger: true },
  ];

  // ── Rows ─────────────────────────────────────────────────────────────────────
  function Row({ cat }: { cat: NoticeCategory }) {
    const [hov, setHov] = useState(false);
    const active = activeFilter === cat.code;
    const dragProps = isAdmin ? {
      draggable: renaming !== cat.code,
      onDragStart: (e: React.DragEvent) => { e.dataTransfer.effectAllowed = "move"; dragId.current = cat.id; },
      onDragOver: (e: React.DragEvent) => { e.preventDefault(); setDropId(cat.id); },
      onDrop: (e: React.DragEvent) => { e.preventDefault(); handleDrop(cat); },
      onDragEnd: () => { dragId.current = null; setDropId(null); },
    } : {};

    // ── 구분선 ──
    if (isDivider(cat)) {
      const sec = parseSection(cat.label);
      if (renaming === cat.code) {
        return (
          <div style={{ padding: "8px 12px 4px" }}>
            <InlineInput initial={sec.text} placeholder="구분선 이름…" onCommit={v => renameCat(cat, v)} onCancel={() => setRenaming(null)} />
          </div>
        );
      }
      return (
        <div {...dragProps}
          onContextMenu={e => { if (isAdmin) { e.preventDefault(); e.stopPropagation(); setCtx({ x: e.clientX, y: e.clientY, cat }); } }}
          onDoubleClick={() => { if (isAdmin) setRenaming(cat.code); }}
          title={isAdmin ? "더블클릭/우클릭으로 편집" : undefined}
          style={{ padding: "9px 12px 4px", cursor: isAdmin ? "grab" : "default", background: dropId === cat.id ? "rgba(99,102,241,0.12)" : "transparent" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {sec.text && <span style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text-3)", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".04em", flexShrink: 0 }}>{sec.text}</span>}
            <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
          </div>
        </div>
      );
    }

    // ── 카테고리(1차) ──
    if (renaming === cat.code) {
      return (
        <div style={{ display: "flex", gap: 4, margin: "1px 6px", padding: "3px 6px", alignItems: "center", background: "var(--surface)", borderRadius: 6 }}>
          <span style={{ fontSize: 13 }}>📁</span>
          <InlineInput initial={cat.label} onCommit={v => renameCat(cat, v)} onCancel={() => setRenaming(null)} />
        </div>
      );
    }
    return (
      <div {...dragProps}
        onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        onContextMenu={e => { if (isAdmin) { e.preventDefault(); e.stopPropagation(); setCtx({ x: e.clientX, y: e.clientY, cat }); } }}
        style={{
          display: "flex", alignItems: "center", gap: 6, margin: "1px 6px", borderRadius: 6,
          background: dropId === cat.id ? "rgba(99,102,241,0.18)" : active ? "rgba(99,102,241,0.12)" : hov ? "rgba(99,102,241,0.05)" : "transparent",
          outline: dropId === cat.id ? "1.5px dashed var(--p-500,#6366f1)" : "none", outlineOffset: -1,
          cursor: isAdmin ? "grab" : "pointer", transition: "background 0.12s",
        }}>
        <button onClick={() => goFilter(cat.code)}
          style={{ flex: 1, display: "flex", alignItems: "center", gap: 7, background: "none", border: "none", cursor: "pointer", padding: "6px 4px 6px 10px", textAlign: "left", minWidth: 0 }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background: active ? "var(--p-500,#6366f1)" : dotColor(cat.color) }} />
          <span style={{ flex: 1, fontSize: 13, fontWeight: active ? 700 : 500, color: active ? "var(--p-600,#4f46e5)" : "var(--text-1,#1f2937)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {cat.label}
          </span>
          {cat.min_rank > 0 && <span title={rankLabel(cat.min_rank)} style={{ fontSize: 9, color: "var(--text-3)", flexShrink: 0 }}>🔒{rankShort(cat.min_rank)}</span>}
        </button>
        {isAdmin && hov && (
          <div style={{ display: "flex", gap: 1, paddingRight: 4 }} onClick={e => e.stopPropagation()}>
            <button title="세부탭 추가" style={iconBtn} onClick={() => addSubTab(cat.id)}>＃</button>
            <button title="이름 변경" style={iconBtn} onClick={() => setRenaming(cat.code)}>✏</button>
            <button title="더 보기(우클릭)" style={iconBtn} onClick={e => setCtx({ x: e.clientX, y: e.clientY, cat })}>⋯</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "14px 12px 10px", borderBottom: "1px solid var(--border)", flexShrink: 0, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "var(--text-1)" }}>공지사항</h3>
          <p style={{ margin: "2px 0 0", fontSize: 11, color: "var(--text-3)" }}>카테고리(1차) · 세부탭은 상단</p>
        </div>
        <button onClick={onCollapse} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", padding: 2, borderRadius: 4 }} title="접기">
          <I.PanelL size={14} />
        </button>
      </div>

      {/* 전체 공지 */}
      <div style={{ padding: "6px 8px 2px", flexShrink: 0 }}>
        <button onClick={() => goFilter(null)}
          style={{ display: "flex", alignItems: "center", gap: 7, width: "100%", border: "none", cursor: "pointer", textAlign: "left", padding: "6px 10px", borderRadius: 6, fontSize: 13, background: onNoticeRoot ? "rgba(99,102,241,0.12)" : "transparent", color: onNoticeRoot ? "var(--p-600,#4f46e5)" : "var(--text-2)", fontWeight: onNoticeRoot ? 600 : 400 }}>
          <span>📋</span><span>전체 공지</span>
        </button>
      </div>

      {/* Tree (1차) — 빈칸 우클릭 시 팝업 메뉴 */}
      <div style={{ flex: 1, overflowY: "auto", padding: "2px 4px" }}
        onContextMenu={e => { if (isAdmin) { e.preventDefault(); setBgCtx({ x: e.clientX, y: e.clientY }); } }}>
        <div style={{ display: "flex", alignItems: "center", padding: "6px 8px 2px 10px" }}>
          <span style={{ flex: 1, fontSize: 10, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>카테고리</span>
          {isAdmin && (
            <button title="카테고리 추가" onClick={() => setAddingTop(true)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", padding: "2px 4px", borderRadius: 3, display: "flex", alignItems: "center" }}
              onMouseOver={e => (e.currentTarget.style.color = "var(--p-500,#6366f1)")}
              onMouseOut={e => (e.currentTarget.style.color = "var(--text-3)")}>
              <I.Plus size={12} />
            </button>
          )}
        </div>

        {topItems.map(c => <Row key={c.id} cat={c} />)}

        {addingTop && (
          <div style={{ display: "flex", gap: 4, margin: "2px 6px", padding: "4px 8px", alignItems: "center", background: "rgba(99,102,241,0.07)", borderRadius: 6, border: "1px dashed var(--p-400,#818cf8)" }}>
            <span style={{ fontSize: 12 }}>📁</span>
            <InlineInput placeholder="카테고리 이름…" onCommit={addCategory} onCancel={() => setAddingTop(false)} />
          </div>
        )}

        {topItems.length === 0 && !addingTop && (
          <div style={{ padding: "16px 12px", color: "var(--text-3)", fontSize: 12, textAlign: "center", lineHeight: 1.7 }}>
            {isAdmin ? "빈 곳을 우클릭하거나 위 + 버튼으로\n카테고리·구분선을 추가하세요" : "카테고리가 없습니다"}
          </div>
        )}
      </div>

      {ctx && <ContextMenu x={ctx.x} y={ctx.y} items={ctxItems(ctx.cat)} onClose={() => setCtx(null)} />}
      {bgCtx && (
        <ContextMenu x={bgCtx.x} y={bgCtx.y} onClose={() => setBgCtx(null)} items={[
          { label: "카테고리 추가", icon: "📁", onClick: () => setAddingTop(true) },
          { label: "구분선 추가", icon: "―", onClick: addDivider },
        ]} />
      )}
    </div>
  );
}
